import { lazy } from "react";
import { createBrowserRouter, type RouteObject, Navigate } from "react-router-dom";
import DashboardLayout from "../pages/(dashboard)/DashboardLayout";
import ErrorBoundary from "../pages/error-boundary";
import ProtectedRoute from "./ProtectedRoute";
import GuestRoute from "./GuestRoute";
import RootLayout from "../pages/RootLayout";

const NotFound = lazy(() => import("../pages/not-found"));
const Dashboard = lazy(() => import("../pages/(dashboard)/index"));
const Analytics = lazy(() => import("../pages/(dashboard)/analytics/index"));
const Settings = lazy(() => import("../pages/(dashboard)/settings/index"));
const Support = lazy(() => import("../pages/(dashboard)/support/index"));
const Notifications = lazy(() => import("../pages/(dashboard)/notifications/index"));
const Widget = lazy(() => import("../pages/(dashboard)/widget/index"));
const Team = lazy(() => import("../pages/(dashboard)/team/index"));
const Billing = lazy(() => import("../pages/(dashboard)/billing/index"));
const Login = lazy(() => import("../pages/(auth)/login/index"));
const Register = lazy(() => import("../pages/(auth)/register/index"));
const VerifyEmail = lazy(() => import("../pages/(auth)/verify-email/index"));
const AuthCallback = lazy(() => import("../pages/(auth)/auth/callback/index"));
const ForgotPassword = lazy(() => import("../pages/(auth)/forgot-password/index"));
const ResetPassword = lazy(() => import("../pages/(auth)/reset-password/index"));
const Payment = lazy(() => import("../pages/payment/index"));
const Onboarding = lazy(() => import("../pages/(auth)/onboarding/index"));
const Live = lazy(() => import("../pages/(dashboard)/live/index"));
const Insights = lazy(() => import("../pages/(dashboard)/insights/index"));
const CRM = lazy(() => import("../pages/(dashboard)/crm/index"));
const Calendar = lazy(() => import("../pages/(dashboard)/calendar/index"));

// SUPER_ADMIN pages
const AdminLayout = lazy(() => import("../pages/(dashboard)/super-admin/index"));
const Companies = lazy(() => import("../pages/(dashboard)/super-admin/companies/index"));
const PlatformAnalytics = lazy(
  () => import("../pages/(dashboard)/super-admin/platform-analytics/index")
);
const SupportAdmin = lazy(() => import("../pages/(dashboard)/super-admin/support/index"));
const BillingOversight = lazy(
  () => import("../pages/(dashboard)/super-admin/billing-oversight/index")
);
const DiscountCodes = lazy(() => import("../pages/(dashboard)/super-admin/discounts/index"));
const ReferralCodes = lazy(() => import("../pages/(dashboard)/super-admin/referrals/index"));
const Trials = lazy(() => import("../pages/(dashboard)/super-admin/trials/index"));

const routes: RouteObject[] = [
  {
    element: <RootLayout />,
    errorElement: <ErrorBoundary />,
    children: [
      {
        path: "/login",
        element: (
          <GuestRoute>
            <Login />
          </GuestRoute>
        ),
      },
      {
        path: "/register",
        element: (
          <GuestRoute>
            <Register />
          </GuestRoute>
        ),
      },
      {
        path: "/verify-email",
        element: <VerifyEmail />,
      },
      {
        path: "/auth/callback",
        element: <AuthCallback />,
      },
      {
        path: "/oauth-complete",
        element: (
          <ProtectedRoute>
            <Navigate to="/onboarding" replace />
          </ProtectedRoute>
        ),
      },
      {
        path: "/forgot-password",
        element: (
          <GuestRoute>
            <ForgotPassword />
          </GuestRoute>
        ),
      },
      {
        path: "/reset-password",
        element: (
          <GuestRoute>
            <ResetPassword />
          </GuestRoute>
        ),
      },
      {
        path: "/payment",
        element: (
          <ProtectedRoute>
            <Payment />
          </ProtectedRoute>
        ),
      },
      {
        path: "/onboarding",
        element: (
          <ProtectedRoute>
            <Onboarding />
          </ProtectedRoute>
        ),
      },
      {
        path: "/",
        element: (
          <ProtectedRoute>
            <DashboardLayout />
          </ProtectedRoute>
        ),
        children: [
          {
            index: true,
            element: <Dashboard />,
          },
          {
            path: "analytics",
            element: <Analytics />,
          },
          {
            path: "live",
            element: <Live />,
          },
          {
            path: "insights",
            element: (
              <ProtectedRoute requireAdmin>
                <Insights />
              </ProtectedRoute>
            ),
          },
          {
            path: "crm",
            element: <CRM />,
          },
          {
            path: "calendar",
            element: <Calendar />,
          },
          {
            path: "settings",
            element: <Settings />,
          },
          {
            path: "support",
            element: <Support />,
          },
          {
            path: "notifications",
            element: <Notifications />,
          },
          {
            path: "widget",
            element: (
              <ProtectedRoute requireAdmin>
                <Widget />
              </ProtectedRoute>
            ),
          },
          {
            path: "team",
            element: (
              <ProtectedRoute requireAdmin>
                <Team />
              </ProtectedRoute>
            ),
          },
          {
            path: "billing",
            element: (
              <ProtectedRoute requireAdmin>
                <Billing />
              </ProtectedRoute>
            ),
          },
          // SUPER_ADMIN routes (nested)
          {
            path: "admin",
            element: (
              <ProtectedRoute requireSuperAdmin>
                <AdminLayout />
              </ProtectedRoute>
            ),
            children: [
              {
                index: true,
                element: <Navigate to="/admin/companies" replace />,
              },
              {
                path: "companies",
                element: <Companies />,
              },
              {
                path: "platform-analytics",
                element: <PlatformAnalytics />,
              },
              {
                path: "support",
                element: <SupportAdmin />,
              },
              {
                path: "billing-oversight",
                element: <BillingOversight />,
              },
              {
                path: "discounts",
                element: <DiscountCodes />,
              },
              {
                path: "referrals",
                element: <ReferralCodes />,
              },
              {
                path: "trials",
                element: <Trials />,
              },
            ],
          },
        ],
      },

      {
        path: "*",
        element: <NotFound />,
      },
    ],
  },
];

export const router = createBrowserRouter(routes);
